import { Checkbox, MenuItem, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDynamicForm } from 'pages/DynamicPageV2/DynamicForm';
import { useNavigate } from 'react-router-dom';
import { ContinueButton } from 'pages/DynamicPageV2/components/ContinueButton';
import { ReactComponent as CheckedIconSvg } from 'assets/images/icons/checked-icon.svg';
import { ReactComponent as UnCheckedIconSvg } from 'assets/images/icons/unchecked-icon.svg';
import tiktokIcon from 'assets/images/icons/tiktok-icon.svg';
import instagramIcon from 'assets/images/icons/instagram-icon.svg';
import youtubeIcon from 'assets/images/icons/youtube-icon.svg';
import level0Icon from 'assets/images/icons/level-0-icon.svg';
import level1Icon from 'assets/images/icons/level-1-icon.svg';
import level2Icon from 'assets/images/icons/level-2-icon.svg';
import level3Icon from 'assets/images/icons/level-3-icon.svg';
import { useEffect } from 'react';
import trackAmplitude from 'services/analytics';
import { CW_SEGMENT } from 'utils/AmplitudeTrackEvents';

const SVG_ICON_MAP = {
  tiktok: tiktokIcon,
  instagram: instagramIcon,
  youtube: youtubeIcon,
  level0: level0Icon,
  level1: level1Icon,
  level2: level2Icon,
  level3: level3Icon,
};

export default function SelectListRenderer({
  pageName,
  quizConfig,
  setIsLoading,
}) {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { setValue, getValue } = useDynamicForm();
  const value = getValue(pageName);

  const {
    questions,
    title,
    tag,
    nextPage,
    subtitle,
    withSubmit,
    multiSelect,
    continueButtonText,
    logQuestionAnswer,
  } = quizConfig[pageName];

  const currentQuestion = questions.find(
    (question) => question.value === value
  );

  const handleChange = (value) => {
    let valueToSet = value;
    if (multiSelect) {
      const currentValues = getValue(pageName) || [];
      let filteredValues = currentValues.filter((v) => v !== value);
      if (filteredValues.length === currentValues.length) {
        filteredValues = [...filteredValues, value];
      }
      valueToSet = filteredValues;
    }
    setValue(pageName, valueToSet);
    trackAmplitude(`${quizConfig?.[pageName]?.eventName}_answer`, {
      value: valueToSet,
    });

    if (logQuestionAnswer) {
      trackAmplitude(CW_SEGMENT, {
        cw_p1: pageName,
        cw_p2: valueToSet,
      });
    }

    if (!withSubmit) {
      navigate(`./${nextPage}`);
    }
  };

  useEffect(() => {
    trackAmplitude(quizConfig?.[pageName]?.eventName);
    setIsLoading(false);
  }, [setIsLoading, pageName, quizConfig]);

  return (
    <Stack gap={2.5} mt={2.5}>
      <Stack gap={0.75}>
        <Typography fontWeight={600} color="primary">
          {t(tag)}
        </Typography>
        <Typography variant="h1">{t(title)}</Typography>
        <Typography color="text.secondary" mt={0.75}>
          {t(subtitle)}
        </Typography>
      </Stack>
      <Stack gap={1.25}>
        {questions.map((question) => {
          const isSelected = multiSelect
            ? (value?.includes(question.value) ?? false)
            : question.value === value;
          return (
            <MenuItem
              key={question.value}
              selected={withSubmit && isSelected}
              onClick={() => handleChange(question.value)}
              sx={{
                overflow: 'hidden',
              }}
            >
              <Stack
                direction="row"
                justifyContent={'space-between'}
                alignItems={'center'}
                width="100%"
                gap={2}
              >
                <Stack direction="row" alignItems={'center'} gap={2}>
                  <QuestionIcon
                    icon={question.icon}
                    iconType={question.iconType}
                  />
                  <Typography variant="inherit" whiteSpace={'break-spaces'}>
                    {Array.isArray(question.label)
                      ? question.label.map((label) => t(label)).join(' ')
                      : t(question.label)}
                  </Typography>
                </Stack>
                {multiSelect && (
                  <Checkbox
                    checked={isSelected}
                    icon={<UnCheckedIconSvg />}
                    checkedIcon={<CheckedIconSvg />}
                    sx={{
                      p: 0,
                    }}
                  />
                )}
              </Stack>
            </MenuItem>
          );
        })}
      </Stack>
      {withSubmit && value && (
        <ContinueButton
          pageName={pageName}
          text={t(continueButtonText)}
          beforeSlot={
            currentQuestion?.details && (
              <Paper sx={{ mb: 1.5 }} variant="success">
                <Typography fontWeight={600}>
                  {t(currentQuestion.details)}
                </Typography>
              </Paper>
            )
          }
        />
      )}
    </Stack>
  );
}

const QuestionIcon = ({ icon, iconType }) => {
  if (iconType === 'svg') {
    const src = SVG_ICON_MAP[icon];
    if (!src) return null;
    return <img src={src} alt="question icon" />;
  }

  if (!icon) return null;
  return <span>{icon}</span>;
};
